import { useState, useCallback, useRef, useEffect } from 'react'
import { ArrowLeft, ArrowRight, RotateCw, ExternalLink, Globe, AlertTriangle } from 'lucide-react'
import Panel from './Panel'

interface LocalBrowserPanelProps {
  initialUrl?: string
  isMaximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  closeable?: boolean
}

export default function LocalBrowserPanel({
  initialUrl,
  isMaximized,
  onMaximize,
  onClose,
  closeable = true,
}: LocalBrowserPanelProps) {
  // URL state
  const [url, setUrl] = useState(initialUrl || '')
  const [inputUrl, setInputUrl] = useState(initialUrl || '')
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // History for back/forward navigation
  const [history, setHistory] = useState<string[]>(initialUrl ? [initialUrl] : [])
  const [historyIndex, setHistoryIndex] = useState(initialUrl ? 0 : -1)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Normalize URL (add https:// if missing)
  const normalizeUrl = useCallback((rawUrl: string): string => {
    let normalized = rawUrl.trim()
    if (!normalized) return ''
    if (!/^https?:\/\//i.test(normalized)) {
      normalized = 'https://' + normalized
    }
    return normalized
  }, [])

  // Navigate to a URL
  const navigateTo = useCallback((targetUrl: string, addToHistory = true) => {
    const normalized = normalizeUrl(targetUrl)
    if (!normalized) return

    setUrl(normalized)
    setInputUrl(normalized)
    setIsLoading(true)
    setHasError(false)
    setErrorMessage(null)

    if (addToHistory) {
      // Truncate forward history and add new URL
      const newHistory = [...history.slice(0, historyIndex + 1), normalized]
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    }
  }, [normalizeUrl, history, historyIndex])

  // Handle URL submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    navigateTo(inputUrl)
  }, [inputUrl, navigateTo])

  // Navigation controls
  const canGoBack = historyIndex > 0
  const canGoForward = historyIndex < history.length - 1

  const goBack = useCallback(() => {
    if (!canGoBack) return
    const newIndex = historyIndex - 1
    setHistoryIndex(newIndex)
    const targetUrl = history[newIndex]
    setUrl(targetUrl)
    setInputUrl(targetUrl)
    setIsLoading(true)
    setHasError(false)
    setErrorMessage(null)
  }, [canGoBack, historyIndex, history])

  const goForward = useCallback(() => {
    if (!canGoForward) return
    const newIndex = historyIndex + 1
    setHistoryIndex(newIndex)
    const targetUrl = history[newIndex]
    setUrl(targetUrl)
    setInputUrl(targetUrl)
    setIsLoading(true)
    setHasError(false)
    setErrorMessage(null)
  }, [canGoForward, historyIndex, history])

  const refresh = useCallback(() => {
    if (!url) return
    setIsLoading(true)
    setHasError(false)
    setErrorMessage(null)
    // Force iframe reload by toggling src
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src
      iframeRef.current.src = ''
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc
        }
      }, 50)
    }
  }, [url])

  const openInNewTab = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }, [url])

  // Handle iframe load
  const handleLoad = useCallback(() => {
    setIsLoading(false)
    setHasError(false)
    setErrorMessage(null)
  }, [])

  // Handle iframe error - most sites block iframes, so we show a friendly message
  const handleError = useCallback(() => {
    setIsLoading(false)
    setHasError(true)
    setErrorMessage('This site cannot be displayed in an embedded frame.')
  }, [])

  // Detect X-Frame-Options errors via CSP violation
  useEffect(() => {
    const handleSecurityPolicyViolation = (e: SecurityPolicyViolationEvent) => {
      if (e.blockedURI && url && e.blockedURI.includes(new URL(url).hostname)) {
        setHasError(true)
        setErrorMessage('This site blocks embedding in iframes for security reasons.')
        setIsLoading(false)
      }
    }

    document.addEventListener('securitypolicyviolation', handleSecurityPolicyViolation)
    return () => {
      document.removeEventListener('securitypolicyviolation', handleSecurityPolicyViolation)
    }
  }, [url])

  // Auto-navigate to initialUrl on mount
  useEffect(() => {
    if (initialUrl && !url) {
      navigateTo(initialUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Navigate when initialUrl prop changes (e.g., branch switch)
  useEffect(() => {
    if (initialUrl && initialUrl !== url) {
      navigateTo(initialUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl])

  return (
    <Panel
      title="Preview"
      icon={<Globe className="w-4 h-4" />}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      onClose={onClose}
      closeable={closeable}
    >
      <div className="flex flex-col h-full bg-background">
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-secondary/30">
          {/* Navigation buttons */}
          <button
            onClick={goBack}
            disabled={!canGoBack}
            className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goForward}
            disabled={!canGoForward}
            className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Go forward"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={refresh}
            disabled={!url || isLoading}
            className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Refresh"
          >
            <RotateCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* URL input */}
          <form onSubmit={handleSubmit} className="flex-1 mx-2">
            <input
              ref={inputRef}
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="Enter URL..."
              className="w-full px-3 py-1 text-sm rounded bg-background border border-border focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </form>

          {/* Open in new tab */}
          <button
            onClick={openInNewTab}
            disabled={!url}
            className="p-1.5 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>

        {/* Content area */}
        <div className="flex-1 relative overflow-hidden">
          {/* Empty state */}
          {!url && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
              <Globe className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-sm">Enter a URL to preview</p>
            </div>
          )}

          {/* Loading overlay */}
          {url && isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <RotateCw className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
            </div>
          )}

          {/* Error overlay */}
          {url && hasError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10">
              <AlertTriangle className="w-8 h-8 text-yellow-500 mb-3" />
              <p className="text-sm text-muted-foreground mb-4 text-center px-4">
                {errorMessage || 'Unable to load this page'}
              </p>
              <button
                onClick={openInNewTab}
                className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open in New Tab
              </button>
            </div>
          )}

          {/* iframe */}
          {url && (
            <iframe
              ref={iframeRef}
              src={url}
              className="w-full h-full border-0"
              onLoad={handleLoad}
              onError={handleError}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
              allow="clipboard-read; clipboard-write"
              title="Preview"
            />
          )}
        </div>
      </div>
    </Panel>
  )
}
